/**
 * Unified Configuration Manager for AfterService digital assistant
 * Consolidates all configuration sources with fallback mechanisms
 * Maintains backward compatibility while simplifying management
 */

// Helper functions for type conversion
const parseBoolean = (value) => {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return Boolean(value);
};

const parseInteger = (value, defaultValue = 0) => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

const parseFloatSafe = (value, defaultValue = 0.0) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Unified Configuration Object
 * Single source of truth for all application configuration
 */
const createConfig = () => {
  const config = {
    // Environment
    environment: process.env.NODE_ENV || 'development',
    
    // API Configuration - Using GenAI Chat API for all operations
    api: {
      // GenAI Chat API (for sessions, user profiles, chat, etc.)
      endpoint: process.env.REACT_APP_API_ENDPOINT || 'https://x14bncspba.execute-api.ap-southeast-2.amazonaws.com/prod',
      // Same endpoint for chat (for consistency)
      chatEndpoint: process.env.REACT_APP_CHAT_API_ENDPOINT || 'https://x14bncspba.execute-api.ap-southeast-2.amazonaws.com/prod',
      // Entitlements Lambda Function URL - bypasses API GW 29s timeout
      entitlementsEndpoint: process.env.REACT_APP_ENTITLEMENTS_ENDPOINT || 'https://czzdiagv43vf6nzvd6h2yjsl3m0yxrky.lambda-url.ap-southeast-2.on.aws',
      // Claims Lambda Function URL - bypasses API GW 29s timeout
      claimsApiEndpoint: process.env.REACT_APP_CLAIMS_API_ENDPOINT || 'https://gcrhz6gwqlvt7ket62akerkeim0sfwdq.lambda-url.ap-southeast-2.on.aws',
      // CMO audit 2026-08-15 (§2.1) - `appealsEndpoint` deleted. It pointed at
      // a public AuthType=NONE Function URL that the frontend never called and
      // that had zero consumers anywhere in src/. Appeals go through API
      // Gateway so the Cognito authorizer populates the request context the
      // Lambda reads (PrecedentSearch.js:56-61, AppealLetterGenerator.js:66-70).
      // The Function URL itself has been removed from the account.
      // Claims status CRUD Lambda URL - when empty, falls back to chatEndpoint
      claimsStatusEndpoint: process.env.REACT_APP_CLAIMS_STATUS_ENDPOINT || '',
      // Document Vault Lambda URL - when empty, documentService.js resolves from chatEndpoint
      documentsEndpoint: process.env.REACT_APP_DOCUMENTS_ENDPOINT || '',
      // Smart Notifications Lambda URL - when empty, notificationService.js falls back to chatEndpoint + /notifications
      notificationsEndpoint: process.env.REACT_APP_NOTIFICATIONS_ENDPOINT || '',
      // Events Calendar Lambda URL - when empty, eventsService.js falls back to chatEndpoint + /events
      eventsEndpoint: process.env.REACT_APP_EVENTS_ENDPOINT || '',
      // AfterService Connection Model Lambda URL - when empty, connectionService.js falls back to endpoint + /connections
      connectionsEndpoint: process.env.REACT_APP_CONNECTIONS_ENDPOINT || '',
      // Advocate AgentCore Lambda URL - when empty, falls back to chatEndpoint + /advocate
      advocateEndpoint: process.env.REACT_APP_ADVOCATE_ENDPOINT || '',
      // Privacy-preserving funnel analytics (P3-11) - when empty, monitoringService.trackFunnel
      // drops events client-side (no network call ever leaves the browser). Points at the
      // API Gateway stage fronting analytics_events_handler_v1 (POST /analytics/events).
      analyticsEndpoint: process.env.REACT_APP_ANALYTICS_ENDPOINT || '',
      region: process.env.REACT_APP_API_REGION || 'ap-southeast-2',
      timeout: parseInteger(process.env.REACT_APP_API_TIMEOUT, 60000),
      retryCount: parseInteger(process.env.REACT_APP_API_RETRY_COUNT, 2),
      retryDelay: parseInteger(process.env.REACT_APP_API_RETRY_DELAY, 2000)
    },

    // AWS Cognito Configuration
    cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'ap-southeast-2_cJFAMPlBn',
      userPoolClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID || '6ofh0jvh5rb8qoh2neuj160tfl',
      domain: process.env.REACT_APP_COGNITO_DOMAIN || 'afterservice.auth.ap-southeast-2.amazoncognito.com',
      region: process.env.REACT_APP_COGNITO_REGION || 'ap-southeast-2'
    },

            // Bedrock Agent Configuration
        bedrock: {
          // Phase 999.109/110: V3 supervisor (OXPOZJRDSM) + alias
          // (OG9UMV3FDZ) deleted. chat-handler-v9 + advocate-handler-v2
          // read the AgentCore runtime ARN from SSM, not these headers.
          // Fallbacks neutralized to '' - the conditional spread in
          // apiService.js (lines 516-520) skips the X-Bedrock-* header
          // when the value is falsy, so no dead header is sent.
          agentId: process.env.REACT_APP_BEDROCK_AGENT_ID || '',
          agentAlias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || '',
      modelId: process.env.REACT_APP_BEDROCK_MODEL_ID || 'anthropic.claude-3-sonnet-20240229-v1:0',
      maxTokens: parseInteger(process.env.REACT_APP_BEDROCK_MAX_TOKENS, 4096),
      temperature: parseFloatSafe(process.env.REACT_APP_BEDROCK_TEMPERATURE, 0.7)
    },

    // Feature Flags: Phase 999.100 audit 2026-05-11 A4 - legacy block deleted.
    // The canonical feature-flag surface is `config/envConfig.js::features`,
    // which is what every caller of `getFeatureFlags()` actually imports
    // (verified by grep across src/frontend/src at audit time). The legacy
    // block here omitted the Phase 999.100 W1 regulatory flags and was accidentally
    // fail-closed only because reading an undefined property returned a
    // falsy value. Removed to prevent future foot-guns.

    // Monitoring Configuration
    monitoring: {
      enableXRay: parseBoolean(process.env.REACT_APP_ENABLE_XRAY ?? 'true'),
      logLevel: process.env.REACT_APP_LOG_LEVEL || 'info',
      enableRequestTracking: parseBoolean(process.env.REACT_APP_ENABLE_REQUEST_TRACKING ?? 'true'),
      enableErrorReporting: parseBoolean(process.env.REACT_APP_ENABLE_ERROR_REPORTING ?? 'true'),
      sampleRate: parseFloatSafe(process.env.REACT_APP_MONITORING_SAMPLE_RATE, 0.1)
    },

    // Chat Configuration
    chat: {
      maxMessageLength: parseInteger(process.env.REACT_APP_MAX_MESSAGE_LENGTH, 4000),
      minMessageLength: parseInteger(process.env.REACT_APP_MIN_MESSAGE_LENGTH, 2),
      rateLimitDelay: parseInteger(process.env.REACT_APP_RATE_LIMIT_DELAY, 1000),
      maxSessionIdLength: parseInteger(process.env.REACT_APP_MAX_SESSION_ID_LENGTH, 100),
      historyLimit: parseInteger(process.env.REACT_APP_CHAT_HISTORY_LIMIT, 100)
    },

    // Theme Configuration
    theme: {
      primary: process.env.REACT_APP_THEME_PRIMARY || '#5086ed',
      secondary: process.env.REACT_APP_THEME_SECONDARY || '#FFB300',
      background: process.env.REACT_APP_THEME_BACKGROUND || '#F8F9FA',
      textPrimary: process.env.REACT_APP_THEME_TEXT_PRIMARY || '#1A1A1A',
      textSecondary: process.env.REACT_APP_THEME_TEXT_SECONDARY || '#666666',
      fontFamily: process.env.REACT_APP_THEME_FONT_FAMILY || 'Inter'
    },

    // WebSocket V2 Configuration
    websocket: {
      url: process.env.REACT_APP_WEBSOCKET_URL || 'wss://rktto0da91.execute-api.ap-southeast-2.amazonaws.com/prod',
      region: process.env.REACT_APP_WEBSOCKET_REGION || 'ap-southeast-2',
      reconnectAttempts: parseInteger(process.env.REACT_APP_WEBSOCKET_RECONNECT_ATTEMPTS, 3),
      reconnectInterval: parseInteger(process.env.REACT_APP_WEBSOCKET_RECONNECT_INTERVAL, 2000),
      heartbeatInterval: parseInteger(process.env.REACT_APP_WEBSOCKET_HEARTBEAT_INTERVAL, 30000),
      connectionTimeout: parseInteger(process.env.REACT_APP_WEBSOCKET_CONNECTION_TIMEOUT, 10000),
      enabled: parseBoolean(process.env.REACT_APP_WEBSOCKET_ENABLED ?? 'true'),
      authEnabled: parseBoolean(process.env.REACT_APP_WEBSOCKET_AUTH_ENABLED ?? 'true'),
      authHeaderName: process.env.REACT_APP_WEBSOCKET_AUTH_HEADER || 'Authorization',
      streaming: {
        enabled: parseBoolean(process.env.REACT_APP_WEBSOCKET_STREAMING_ENABLED ?? 'true'),
        chunkProcessing: parseBoolean(process.env.REACT_APP_WEBSOCKET_CHUNK_PROCESSING ?? 'true'),
        showTypingIndicator: parseBoolean(process.env.REACT_APP_WEBSOCKET_SHOW_TYPING ?? 'true'),
        minDisplayTime: parseInteger(process.env.REACT_APP_WEBSOCKET_MIN_DISPLAY_TIME, 500),
        maxChunkDelay: parseInteger(process.env.REACT_APP_WEBSOCKET_MAX_CHUNK_DELAY, 100),
        autoComplete: parseBoolean(process.env.REACT_APP_WEBSOCKET_AUTO_COMPLETE ?? 'true')
      }
    },

    // Performance Configuration
    performance: {
      bundleOptimization: parseBoolean(process.env.REACT_APP_BUNDLE_OPTIMIZATION ?? 'true'),
      lazyLoading: parseBoolean(process.env.REACT_APP_LAZY_LOADING ?? 'true'),
      cacheMaxAge: parseInteger(process.env.REACT_APP_CACHE_MAX_AGE, 300000), // 5 minutes
      preloadRoutes: parseBoolean(process.env.REACT_APP_PRELOAD_ROUTES ?? 'false')
    }
  };

  return config;
};

/**
 * Configuration validation
 * Ensures all required values are present and valid
 */
const validateConfig = (config) => {
  const errors = [];
  const warnings = [];

  // Validate required API configuration
  if (!config.api.endpoint) {
    errors.push('API endpoint is required');
  } else if (!config.api.endpoint.startsWith('https://')) {
    warnings.push('API endpoint should use HTTPS in production');
  }

  // Validate required Cognito configuration
  if (!config.cognito.userPoolId) {
    errors.push('Cognito User Pool ID is required');
  }
  if (!config.cognito.userPoolClientId) {
    errors.push('Cognito User Pool Client ID is required');
  }

  // Phase 999.109/110: V3 supervisor + alias deleted in AWS. AgentCore
  // runtime is read server-side from SSM, so the frontend no longer needs
  // an agent ID/alias. Validation block removed accordingly.

  // Validate performance settings
  if (config.chat.maxMessageLength < config.chat.minMessageLength) {
    errors.push('Max message length must be greater than min message length');
  }

  // Log validation results
  if (warnings.length > 0) {
    console.warn('⚠️ Configuration warnings:', warnings);
  }

  if (errors.length > 0) {
    console.error('❌ Configuration validation failed:', errors);
    throw new Error(`Configuration validation failed: ${errors.join(', ')}`);
  }

  return true;
};

/**
 * Get Amplify configuration
 * Converts unified config to Amplify format
 */
const getAmplifyConfig = (config) => {
  return {
    Auth: {
      Cognito: {
        userPoolId: config.cognito.userPoolId,
        userPoolClientId: config.cognito.userPoolClientId,
        region: config.cognito.region,
        loginWith: {
          oauth: {
            domain: config.cognito.domain,
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [
              'http://localhost:3000/',
              'https://d21ar8rqmxxz1f.cloudfront.net/'
            ],
            redirectSignOut: [
              'http://localhost:3000/',
              'https://d21ar8rqmxxz1f.cloudfront.net/'
            ],
            responseType: 'code'
          }
        }
      }
    },
    API: {
      REST: {
        AfterServiceAPI: {
          endpoint: config.api.endpoint,
          region: config.api.region
        }
      }
    }
  };
};

/**
 * Initialize configuration
 * Creates, validates, and returns the configuration object
 */
const initializeConfig = () => {
  try {
    const config = createConfig();
    validateConfig(config);
    return config;
  } catch (error) {
    console.error('❌ Configuration initialization failed:', error);
    throw error;
  }
};

// Initialize configuration immediately
const appConfig = initializeConfig();

// Export configuration sections
export const getApiConfig = () => appConfig.api;
export const getCognitoConfig = () => appConfig.cognito;
export const getBedrockConfig = () => appConfig.bedrock;
// Phase 999.100 audit 2026-05-11 A4 - getFeatureFlags export removed.
// Use `import { getFeatureFlags } from './envConfig'` instead (canonical
// W1 flag surface). All existing callers already do; the legacy export here
// was dead since 999.100 W1.
export const getMonitoringConfig = () => appConfig.monitoring;
export const getChatConfig = () => appConfig.chat;
export const getThemeConfig = () => appConfig.theme;
export const getWebSocketConfig = () => appConfig.websocket;
export const getPerformanceConfig = () => appConfig.performance;

// Export full configuration
export const getConfig = () => appConfig;

// Export Amplify configuration
export const getAmplifyConfiguration = () => getAmplifyConfig(appConfig);

// Export initialization function for dynamic reconfiguration
export { initializeConfig };

// Default export
export default {
  config: appConfig,
  getApiConfig,
  getCognitoConfig,
  getBedrockConfig,
  // getFeatureFlags removed per Phase 999.100 audit 2026-05-11 A4.
  getMonitoringConfig,
  getChatConfig,
  getThemeConfig,
  getPerformanceConfig,
  getConfig,
  getAmplifyConfiguration,
  initializeConfig
}; 